import { useMemo } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import type { AsyncSelectProps, AsyncSelectOption, AsyncSelectItem } from './types';

export const AsyncSelect = <T extends AsyncSelectItem>({
  currentValueData,
  searchData,
  isLoading,
  value,
  searchTerm = '',
  onSearch,
  excludeIds = [],
  minSearchLength = 2,
  searchHint,
  placeholder,
  allowClear = true,
  ...restProps
}: AsyncSelectProps<T>) => {
  const { t } = useTranslation();
  const options = useMemo<AsyncSelectOption<T>[]>(() => {
    const result: AsyncSelectOption<T>[] = [];

    if (currentValueData && value) {
      const valueArray = Array.isArray(value) ? value : [value];
      const currentItems = currentValueData.filter((item) => valueArray.includes(item.id));

      currentItems.forEach((currentItem) => {
        const isInSearchResults = searchData?.some((item) => item.id === currentItem.id);
        if (!isInSearchResults) {
          result.push({
            value: currentItem.id,
            label: currentItem.name,
            data: currentItem,
          });
        }
      });
    }

    if (searchData) {
      const searchOptions = searchData
        .filter((item) => !excludeIds.includes(item.id))
        .map((item) => ({
          value: item.id,
          label: item.name,
          data: item,
        }));
      result.push(...searchOptions);
    }

    return result;
  }, [currentValueData, searchData, value, excludeIds]);

  const defaultPlaceholder = placeholder ?? t('common.select');
  const defaultSearchHint = searchHint ?? t('common.searchHint', { count: minSearchLength });
  const notFoundContent =
    searchTerm.trim().length < minSearchLength ? defaultSearchHint : t('common.noResults');

  return (
    <Select
      {...restProps}
      style={{ width: '100%', ...restProps.style }}
      value={value}
      placeholder={defaultPlaceholder}
      allowClear={allowClear}
      showSearch={{
        onSearch: onSearch,
        filterOption: false,
      }}
      loading={isLoading}
      options={options}
      notFoundContent={notFoundContent}
    />
  );
};
